<template>
  <wui-chip
    :label="displayText"
    :title="contactName"
    :icon-prepend="icon"
    :size="24"
    :variant="isForTemporary ? 'neut' : 'brand'"
    :removable="!disabled"
    class="max-w-full"
    @remove="emit('delete')"
  />
</template>

<script setup lang="ts" generic="T">
import { SelectItem, IconName, WuiChip } from '@wui/common-library'
import { computed } from 'vue'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'

const props = defineProps<{
  participant: SelectItem<T>
  icon: IconName
  isForTemporary?: boolean
  disabled?: boolean
  idKey?: keyof T | (keyof T)[]
  displaySuffix?: string
}>()

const emit = defineEmits<{
  (event: 'delete'): void
}>()

const { currentUser } = useCurrentUser()
const { t } = useLocalization()

const getIdentityByKeys = (
  record: Record<string, unknown>,
  idKeys: (keyof T)[],
): unknown => {
  for (const key of idKeys) {
    const value = record[key as string]
    if (typeof value === 'string') {
      if (value.trim()) {
        return value
      }
      continue
    }
    if (value !== null && value !== undefined) {
      return value
    }
  }

  return idKeys.length ? record[idKeys[0] as string] : undefined
}

const contactName = computed(() => {
  const idKeys = props.idKey
    ? (Array.isArray(props.idKey) ? props.idKey : [props.idKey])
    : []

  let currentUserIdentity: unknown
  if (currentUser.value) {
    if (!idKeys.length) {
      currentUserIdentity = currentUser.value.internalNumber
    } else {
      currentUserIdentity = getIdentityByKeys(
        currentUser.value as unknown as Record<string, unknown>,
        idKeys,
      )
    }
  }

  const participantIdentity = idKeys.length
    ? getIdentityByKeys(props.participant.value as unknown as Record<string, unknown>, idKeys)
    : props.participant.value

  if (currentUser.value && participantIdentity === currentUserIdentity) {
    return `${currentUser.value.name} (${t('common.you')})`
  }

  return props.participant.label ? props.participant.label : t('common.unknown')
})

const displayText = computed(() => {
  if (!props.displaySuffix) return contactName.value
  return `${contactName.value} (${props.displaySuffix})`
})
</script>
